import { useEffect, useRef, useState, MouseEvent } from 'react'

import CellValue from 'components/ui/cell/__value'
import CellHeading from 'components/ui/cell/__heading'
import Search from 'components/ui/search'

import DepositCard from 'pages/account/components/deposit-card'

import { AddAddressPopup } from 'pages/account/settings/components/white-list/components/add-address-popup'

import FilterButton from 'pages/account/components/deposit-card/header/filter-button'
import { AddFilterPopup } from 'pages/account/components/add-filter-popup'

import { WhiteListItem } from 'pages/account/settings/components/white-list/components/white-list-item'
import { _fetchWhitelistWallets } from 'store/fetchers/wallets'

import cn from 'classnames'

import { useTranslation } from 'utils/use-translation'

import { ChosenFiltersTabs } from '../../../components/mobile-choosen-filters-tabs'

import { WhiteListItem as IWhiteListItem } from '../../../../../store/reducers/settings'

import BorderedButton from '../../../../../components/global/bordered-button'

import styles from './styles.module.scss'
import PlusIcon from './icons/plus.svg'
import EmptyIcon from './icons/empty.svg'
import { DeleteAddressPopup } from './components/delete-address-popup'

export const WhiteList = ({ openPopup }: { openPopup?: () => void }) => {
  const { t } = useTranslation()

  const [addressPopupIsVisible, setAddressPopupIsVisible] = useState(false)
  const [filterPopupIsVisible, setFilterPopupIsVisible] = useState(false)
  const [whiteList, setWhiteList] = useState<IWhiteListItem[]>([])
  const [filterByAddressName, setFilterByAddressName] = useState<number>(0)
  const [filterByAssetName, setFilterByAssetName] = useState<number>(1)
  const [sortedWhiteLists, setSortedWhiteLists] = useState<IWhiteListItem[]>([])
  const currentWallets = useRef<IWhiteListItem[]>([])
  const [searchValue, setSearchValue] = useState<string>('')

  const [pageNumbers, setPageNumbers] = useState<number>(0)
  const [pageIndex, setPageIndex] = useState<number>(1)
  const [firstNumbers, setFirstNumbers] = useState<number[]>([])
  const [firstIndex, setFirstIndex] = useState<number>(0)
  const [lastIndex, setLastIndex] = useState<number>(10)
  const [isNextBtnActive, setIsNextBtnActive] = useState<boolean>(true)
  const [isPrevBtnActive, setIsPrevBtnActive] = useState<boolean>(false)
  const [isSearchEmpty, setIsSearchEmpty] = useState<boolean>(true)
  const isLoaded = useRef<boolean>(false)
  const [deleteAddressPopupVisible, setDeleteAddressPopupVisible] = useState<IWhiteListItem>(null)

  const refetch = () => {
    _fetchWhitelistWallets().then((list) => {
      setWhiteList(list)
      setSortedWhiteLists(list)
    })
  }

  useEffect(() => {
    if (whiteList.length) {
      const test = [...whiteList]
      setSortedWhiteLists(test)
      currentWallets.current = whiteList
    }
  }, [whiteList])

  useEffect(() => {
    setPageNumbers(Math.ceil(sortedWhiteLists.length / 10))
  }, [sortedWhiteLists])

  useEffect(() => {
    if (pageNumbers < 4) {
      setFirstNumbers(Array.from({ length: pageNumbers }, (_, index) => index + 1))
    } else {
      if (pageIndex < 3) {
        setFirstNumbers([1, 2, 3])
      } else if (pageIndex >= 3 && pageIndex <= pageNumbers - 2) {
        setFirstNumbers([pageIndex - 1, pageIndex, pageIndex + 1])
      } else {
        setFirstNumbers([pageNumbers - 3, pageNumbers - 2, pageNumbers - 1])
      }
    }

    setIsPrevBtnActive(pageIndex !== 1)
    setIsNextBtnActive(pageIndex !== pageNumbers)
  }, [pageIndex, pageNumbers])

  const goToPage = (event: MouseEvent<HTMLButtonElement>) => {
    const pageNumber = +(event.target as HTMLButtonElement).innerHTML

    if (pageNumber === pageNumbers) {
      setLastIndex(sortedWhiteLists.length - 1)
      setFirstIndex(10 * (pageNumbers - 1))
    }
    setPageIndex(pageNumber)
    setLastIndex(pageNumber * 10)
    setFirstIndex(pageNumber * 10 - 10)
  }

  const nextPage = () => {
    if (pageIndex === pageNumbers) {
      return
    }
    setIsPrevBtnActive(true)

    setFirstIndex((prev) => prev + 10)
    setLastIndex((prev) => prev + 10)
    setPageIndex((prev) => prev + 1)
  }

  const prevPage = () => {
    if (pageIndex === 1) {
      return
    }
    setIsNextBtnActive(true)

    setFirstIndex((prev) => prev - 10)
    setLastIndex((prev) => prev - 10)
    setPageIndex((prev) => prev - 1)
  }

  const sortByAssetName = () => {
    let sortedArray = []
    switch (filterByAssetName) {
      case 0:
        setSortedWhiteLists(isSearchEmpty ? whiteList : sortedWhiteLists)
        currentWallets.current = whiteList
        break
      case 1:
        sortedArray = isSearchEmpty
          ? [...whiteList].sort((a, b) => a.currency.code.localeCompare(b.currency.code))
          : [...sortedWhiteLists].sort((a, b) => a.currency.code.localeCompare(b.currency.code))
        setSortedWhiteLists(sortedArray)
        currentWallets.current = sortedArray
        break
      case 2:
        sortedArray = isSearchEmpty
          ? [...whiteList].sort((a, b) => b.currency.code.localeCompare(a.currency.code))
          : [...sortedWhiteLists].sort((a, b) => b.currency.code.localeCompare(a.currency.code))
        setSortedWhiteLists(sortedArray)
        currentWallets.current = sortedArray
        break
    }
  }

  const sortByAddressName = () => {
    let nonEmptyAddress, emptyAddress, sortedWhiteListArray
    switch (filterByAddressName) {
      case 0:
        setSortedWhiteLists(isSearchEmpty ? whiteList : sortedWhiteLists)
        currentWallets.current = whiteList
        break
      case 1:
        nonEmptyAddress = (isSearchEmpty ? whiteList : sortedWhiteLists).filter((item) => item.address_name)
        emptyAddress = (isSearchEmpty ? whiteList : sortedWhiteLists).filter((item) => !item.address_name)
        sortedWhiteListArray = [
          ...nonEmptyAddress.sort((a, b) => a.address_name.localeCompare(b.address_name)),
          ...emptyAddress,
        ]
        setSortedWhiteLists(sortedWhiteListArray)
        currentWallets.current = sortedWhiteListArray
        break
      case 2:
        nonEmptyAddress = (isSearchEmpty ? whiteList : sortedWhiteLists).filter((item) => item.address_name)
        emptyAddress = (isSearchEmpty ? whiteList : sortedWhiteLists).filter((item) => !item.address_name)
        sortedWhiteListArray = [
          ...nonEmptyAddress.sort((a, b) => b.address_name.localeCompare(a.address_name)),
          ...emptyAddress,
        ]
        setSortedWhiteLists(sortedWhiteListArray)
        currentWallets.current = sortedWhiteListArray
        break
    }
  }

  useEffect(() => {
    if (whiteList && whiteList.length !== 0) {
      sortByAssetName()
    }
  }, [filterByAssetName, whiteList])

  useEffect(() => {
    sortByAddressName()
  }, [filterByAddressName])

  const searchTextOnChange = (searchValue: string) => {
    setIsSearchEmpty(searchValue.trim() === '')
    setSearchValue(searchValue)

    if (searchValue.trim() === '') {
      setSortedWhiteLists(currentWallets.current)
    } else {
      setSortedWhiteLists(
        currentWallets.current.filter(
          (item) =>
            item.address_name?.toLowerCase().startsWith(searchValue.toLowerCase()) ||
            item.address.toLowerCase().startsWith(searchValue.toLowerCase()) ||
            item.currency.code.toLowerCase().startsWith(searchValue.toLowerCase()) ||
            item.currency.network.toLowerCase().startsWith(searchValue.toLowerCase()) ||
            item.currency.name.toLowerCase().startsWith(searchValue.toLowerCase()),
        ),
      )
    }
    setPageIndex(1)
    setFirstIndex(0)
    setLastIndex(10)
    setIsPrevBtnActive(false)
    setIsNextBtnActive(true)
  }

  useEffect(() => {
    _fetchWhitelistWallets().then((list) => {
      setWhiteList(list)
      isLoaded.current = true
    })
  }, [])

  return (
    <div className={styles.whiteList}>
      {addressPopupIsVisible && (
        <AddAddressPopup openPopup={openPopup} setPopupIsVisible={setAddressPopupIsVisible} refetch={refetch} />
      )}

      {deleteAddressPopupVisible && <DeleteAddressPopup 
        item={deleteAddressPopupVisible} 
        setPopupIsVisible={setDeleteAddressPopupVisible} 
        refetch={refetch} />}

      <CellValue className={styles.header} accent>
        {t('whitelist')}
      </CellValue>
      <CellHeading className={styles.whiteListSubText}>{t('enabling_whitelist_function')}</CellHeading>

      {whiteList.length === 0 && isLoaded.current && (
        <div className={styles.empty}>
          <img className={styles.icon} src={EmptyIcon} alt="empty icon" />
          <div className={styles.empty__main}>
            <h3>{t('you_dont_have_addresses')}</h3>
            <BorderedButton onClick={() => setAddressPopupIsVisible(true)} className={styles.addBtn}>
              <img src={PlusIcon} alt="plus icon" />
              <p>{t('add_address')}</p>
            </BorderedButton>
          </div>
        </div>
      )}
      {whiteList.length !== 0 && (
        <>
          <div className={styles.searchAndFilters}>
            <div className={styles.searchWrapper}>
              <Search
                className={styles.search}
                onChange={searchTextOnChange}
                value={searchValue}
                placeholder={t('search_by_asset_address')}
              />

              <FilterButton
                onClick={() => setFilterPopupIsVisible(true)}
                className={styles.filterIcon}
                isActive={true}
                isSorting
              />
              {filterPopupIsVisible && (
                <AddFilterPopup
                  onClose={() => setFilterPopupIsVisible(false)}
                  sortings={[
                    { label: t('asset_name'), state: filterByAssetName, setState: setFilterByAssetName },
                    { label: t('address_name'), state: filterByAddressName, setState: setFilterByAddressName },
                  ]}
                />
              )}
            </div>

            <BorderedButton onClick={() => setAddressPopupIsVisible(true)} className={styles.addBtn}>
              <img src={PlusIcon} alt="plus icon" />
              <p>{t('add_new_address')}</p>
            </BorderedButton>
          </div>
          {/* ChosenFiltersTabs only mobile */}
          <ChosenFiltersTabs
            filters={[]}
            sortings={[
              { label: t('asset_name'), state: filterByAssetName, setState: setFilterByAssetName },
              { label: t('address_name'), state: filterByAddressName, setState: setFilterByAddressName },
            ]}
            clearAll={() => {
              setFilterByAssetName(1)
              setFilterByAddressName(0)
            }}
          />
        </>
      )}
      {whiteList.length !== 0 && (
        <div className={styles.whiteListAddresses}>
          <div className={styles.heading}>
            <DepositCard.ColumnHeaderCell
              title={t('asset_name')}
              onClick={() => {
                setFilterByAddressName(0)
                setFilterByAssetName((prev) => (prev === 1 ? 2 : 1))
              }}
              sortingState={filterByAssetName}
              className={styles.heading__item}
            />
            <DepositCard.ColumnHeaderCell
              title={t('address_name')}
              onClick={() => {
                setFilterByAssetName(0)
                setFilterByAddressName((prev) => (prev === 1 ? 2 : 1))
              }}
              sortingState={filterByAddressName}
              className={styles.heading__item}
            />
            <DepositCard.ColumnHeaderCell title={t('address')} className={styles.heading__item} />
          </div>
          {!!sortedWhiteLists?.length &&
            sortedWhiteLists
              .slice(firstIndex, lastIndex)
              .map((item, index) => (
                <WhiteListItem
                  isTruncate
                  key={`${index}`}
                  item={item}
                  refetch={refetch}
                  className={styles.whiteListItem}
                  setDeleteAddressPopupVisible={setDeleteAddressPopupVisible}
                />
              ))}
        </div>
      )}
      {pageNumbers > 1 && (
        <div className={styles.pagination}>
          <button
            className={cn(styles.arrow, styles.arrow_left, { [styles.active]: isPrevBtnActive })}
            onClick={prevPage}
          ></button>
          {firstNumbers.map((item) => (
            <button
              onClick={goToPage}
              key={item}
              className={cn(styles.pageButton, { [styles.active]: item === pageIndex })}
            >
              {item}
            </button>
          ))}
          {pageNumbers > 4 && pageIndex <= pageNumbers - 3 && <span>...</span>}
          {pageNumbers > 3 && (
            <button
              onClick={goToPage}
              className={cn(styles.pageButton, { [styles.active]: pageNumbers === pageIndex })}
            >
              {pageNumbers}
            </button>
          )}
          <button
            className={cn(styles.arrow, styles.arrow_right, { [styles.active]: isNextBtnActive })}
            onClick={nextPage}
          ></button>
        </div>
      )}
    </div>
  )
}
